// src/axl2/OcxoParams/A1LayoutOcxoParams.jsx
import React, { useState, useEffect } from 'react';
import { useViewport } from '../../context/ViewportContext';
import { useModelData } from '../../hooks/useModelData';

//It needs for common SendForm data
import { 
  updateField, 
} from '../../store/contactFormSlice';

const A1LayoutOcxoParams = () => {
  const { isMobile } = useViewport();
  const {
    formData,
    selectedFormAdditionalRequirements,
    partNumberConfig,
    currentProduct,
    actions
  } = useModelData();

  // Local state for supply and signal selections
  //const [selectedVoltage, setSelectedVoltage] = useState('');

  // Extract configuration from partNumberConfig or use defaults
  const temperatureRanges = partNumberConfig?.temperatureRanges || [
/*     { code: "6", range: "-10..60ºC" },
    { code: "7", range: "-30..70ºC" },
    { code: "8", range: "-40..80ºC" }, */
  ];

  const frequencyOptions = partNumberConfig?.frequencyOptions || [
/*     { code: "8.192", value: "8.192", label: "8.192 MHz" },
    { code: "10", value: "10", label: "10 MHz" },
    { code: "16.384", value: "16.384", label: "16.384 MHz" },
    { code: "20", value: "20", label: "20 MHz" }, */
  ];

  // Supply types only (HCMOS is implied)
  const supplyTypes = partNumberConfig?.supplyTypes || [
/*     { voltage: "3.3V", value: "3.3V" },
    { voltage: "5V", value: "5V" } */
  ];

  const agingOptions = partNumberConfig?.agingOptions || [
/*     { code: "50", ppbPerMonth: "0.5" },
    { code: "19", ppbPerMonth: "1" },
    { code: "29", ppbPerMonth: "2" },
    { code: "20", ppbPerMonth: "0.2" }, */
  ];

  const stabilityOptions = partNumberConfig?.stabilityOptions || [
/*     { code: "50", value: "0.5", unit: "ppb" },
    { code: "19", value: "1", unit: "ppb" },
    { code: "29", value: "2", unit: "ppb" },
    { code: "39", value: "3", unit: "ppb" },
    { code: "59", value: "5", unit: "ppb" },
    { code: "18", value: "10", unit: "ppb" },
    { code: "28", value: "20", unit: "ppb" },
    { code: "38", value: "30", unit: "ppb" },
    { code: "58", value: "50", unit: "ppb" } */
  ];



  const handleInputChange = (field, value) => {
    switch (field) {
      case 'tempRange':
        actions.setSelectedTemperatureRanges(value);
        break;
      case 'frequency':
        actions.setSelectedFrequency(value);
        break;
      case 'additionalRequirements':
        //actions.setSelectedFrequency(value);
        //console.log('value: ', value)
        actions.setFormAdditionalRequirements(value);
        break;        
      case 'voltage':
        //setSelectedVoltage(value);
        // Always use HCMOS as signal type
        const supplySignalValue = generateSupplySignalValue('HCMOS', value);
        actions.setSelectedSupplySignal(supplySignalValue);
        actions.setSelectedSupply(value);
        break;
      case 'aging':
        actions.setSelectedAgingOptions(value);
        break;
      case 'stability':
        actions.setSelectedStabilityOptions(value);
        break;
      default:
        console.warn(`Unknown field: ${field}`);
    }
  };

  // Generate supplySignal value from signal and voltage selections
  const generateSupplySignalValue = (signal, voltage) => {
    const voltageFormatted = voltage === '3.3V' ? '3p3V' : '5V';
    return `hcmos${voltageFormatted}`;
  };

  // Generate ordering code
  const getOrderingCode = () => {
    const voltage = formData.supply === '3.3V' ? '3' : '5';
    const signalType = 'C'; // Always 'C' for HCMOS
    const freq = formData.frequency;
    
    // Use product name from currentProduct if available
    const productName = currentProduct?.name || 'XBO14S';
    
    return `${productName}-${formData.tempRange}-${formData.stability}-${formData.aging}-${voltage}-${signalType}-${freq}`;
  };

  // Check if voltage is selected
  const isSupplyComplete = formData.supply;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
      {/* Header */}
      <div className="border-b border-gray-200 bg-gray-50 p-6">
        <div className="flex items-center justify-center flex-col">
          <div className="text-center mb-1">
            <h3 className="text-xl md:text-3xl font-bold border-b-4 border-blue-300 inline-block pb-1">
              OCXO Configuration Form
            </h3>
          </div>
          
          <div className="text-center">
            {!isMobile ? (
              <p className="text-gray-600">
                Complete all fields to generate your ordering partnumber code
              </p>
            ) : (
              <p>Complete all fields</p>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-4 px-4 mt-8 pb-4">
        {/* Form Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Temperature Range */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Temperature Range *
              </label>
              {/* Make the same height as "Aging Factor (ppb/month) *" on Desktop mode */}
              <div className="md:h-[10.8rem]">
                <div className="grid grid-cols-2 gap-3">
                {temperatureRanges.map((range, index) => {
                  const isLast = index === temperatureRanges.length - 1;
                  const isOddCount = temperatureRanges.length % 2 === 1;
                  
                  return (
                    <label 
                      key={range.code} 
                      className={`relative ${isOddCount && isLast ? 'col-span-2' : ''}`}
                    >
                      <input
                        type="radio"
                        name="tempRange"
                        value={range.code}
                        checked={formData.tempRange === range.code}
                        onChange={(e) => handleInputChange('tempRange', e.target.value)}
                        className="sr-only peer"
                      />
                      <div className="p-3 text-center border rounded-lg cursor-pointer transition-all peer-checked:border-blue-500 peer-checked:bg-blue-50 hover:bg-gray-50">
                        <div className="font-medium">{range.range}</div>
                        <div className="text-xs text-gray-500 mt-1">Code {range.code}</div>
                      </div>
                    </label>
                  );
                })}
                </div>
              </div>
            </div>
            {/* Frequency - Now as select input */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Frequency (MHz) *
              </label>
              <select
                value={formData.frequency || frequencyOptions[0]?.value || ''}
                onChange={(e) => handleInputChange('frequency', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {frequencyOptions.map(option => (
                  <option key={option.code} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* HCMOS & Supply - Renamed section */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Supply Voltage *
              </label>
              <div className="p-4 rounded-lg border border-gray-200 bg-white">
                
                {/* Single column for Supply Voltage */}
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-3">
                    {supplyTypes.map((supply) => (
                      <label key={supply.value} className="relative block">
                        <input
                          type="radio"
                          name="voltage"
                          value={supply.value}
                          checked={formData.supply === supply.value}
                          onChange={(e) => handleInputChange('voltage', e.target.value)}
                          className="sr-only peer"
                        />
                        <div className={`
                          p-3 text-center border-2 rounded-lg cursor-pointer transition-all 
                          hover:border-gray-300 hover:bg-gray-50
                          border-gray-200
                          ${formData.supply === supply.value 
                            ? ' peer-checked:bg-amber-50'
                            : ' peer-checked:bg-gray-50'
                          }
                        `}>
                          <div className="font-bold text-gray-800">{supply.voltage}</div>
                          {/* <div className="text-xs text-gray-500 mt-0.5">HCMOS</div> */}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6 ">
            {/* Aging Factor */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Aging Factor (ppb/month) *
              </label>
              <div className="grid grid-cols-2 gap-3">
                {agingOptions.map(option => (
                  <label key={option.code} className="relative">
                    {/* console.log(option) */}
                    <input
                      type="radio"
                      name="aging"
                      value={option.code}
                      checked={formData.aging === option.code}
                      onChange={(e) => handleInputChange('aging', e.target.value)}
                      className="sr-only peer"
                    />
                    <div className="p-3 text-center border rounded-lg cursor-pointer transition-all peer-checked:border-amber-500 peer-checked:bg-amber-50 hover:bg-gray-50">
                      <div className="font-medium">{option.ppbPerMonth}</div>
                      <div className="text-xs text-gray-500 mt-1">Code {option.code}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Stability - As select input */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Stability vs Temperature *
              </label>
              <select
                value={formData.stability}
                onChange={(e) => handleInputChange('stability', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select stability</option>
                {stabilityOptions.map(option => (
                  <option key={option.code} value={option.code}>
                    ±{option.value} {option.unit} (Code {option.code})
                  </option>
                ))}
              </select>
            </div>

            {/* Configuration Preview */}
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <div className="text-sm font-semibold text-gray-700 mb-3">Configuration Preview</div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Temperature Range:</span>
                  <span className="font-medium">
                    {temperatureRanges.find(t => t.code === formData.tempRange)?.range || '—'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Frequency:</span>
                  <span className="font-medium">{formData.frequency || '—'} MHz</span>
                </div>
                
                {/* HCMOS Supply entry */}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Supply:</span>
                  <span className={`font-medium ${formData.supply}`}>
                    {formData.supply || '—'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Signal:</span>
                  <span className="font-medium">
                  HCMOS
                  </span>
                </div>                
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Aging Factor:</span>
                  <span className="font-medium">
                    {agingOptions.find(a => a.code === formData.aging)?.ppbPerMonth || '—'} ppb/month
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Stability:</span>
                  <span className="font-medium">
                    ±{stabilityOptions.find(s => s.code === formData.stability)?.value || '—'} ppb
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Ordering Code & Actions */}
        <div className="mt-8 pt-8 border-t border-gray-200">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
            <div className="text-center mb-6">
              <div className="text-sm text-gray-600 mb-4">Your Ordering Partnumber Code</div>
              <div className="font-mono text-lg md:text-4xl font-bold text-blue-600 break-all">
                {formData.partNumber}
              </div>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Additional requirements
          </label>
          <textarea
            name="additionalRequirements"
            onChange={(e) => handleInputChange('additionalRequirements', e.target.value)}
            value={selectedFormAdditionalRequirements}
            rows="3"
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            placeholder="Brief description of your application or project..."
          />
        </div>
      </div>
    </div>
  );
};

export default A1LayoutOcxoParams;